import { AuthRepository } from 'src/core/authentication/domain/auth-repository';
import { UserInfo } from 'src/core/authentication/domain/user-info';
import { LoginInfo } from 'src/core/authentication/domain/login-info';
import { OriginInfo } from 'src/core/authentication/domain/origin-info';
import { Injectable } from '@angular/core';
import { ApiAuthUserResponse } from './api-auth-user-response';
import { ApiAuthLoginResponse } from './api-auth-login-response';
import { AuthInfo } from 'src/core/authentication/domain/auth-info';
import { AuthToken } from 'src/core/authentication/domain/auth-token';
import { RefreshToken } from 'src/core/authentication/domain/refresh-token';
import { HttpClient } from 'src/core/common/domain/http/http-client';
import { ApiService } from 'src/core/api/domain/api.service';
import { ExceptionManagerService } from 'src/core/common/domain/exceptions/exception-manager.service';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { InvalidCredentialsException } from 'src/core/authentication/domain/exceptions/invalid-credentials-exception';
import { ApiAccessService } from 'src/core/common/domain/api-access/api-access.service';
import { NoConnectionException } from 'src/core/common/domain/exceptions/no-connection-exception';
import { ConnectivityService } from 'src/core/connection/domain/connectivity.service';
import { MasterDataStructureInvalidException } from 'src/core/common/domain/exceptions/master-data-structure-invalid-exception';
import { AttendanceConfiguration } from 'src/core/authentication/domain/attendance-configuration';
import { DeviceService } from 'src/core/device/device.service';

@Injectable({
  providedIn: 'any',
})
export class HttpAuthRepository extends AuthRepository {
  constructor(
    private readonly apiAccessService: ApiAccessService,
    private readonly apiService: ApiService,
    private readonly client: HttpClient,
    private readonly deviceService: DeviceService,
    private readonly connectivityService: ConnectivityService,
    private readonly exceptionManager: ExceptionManagerService
  ) {
    super();
  }

  async login(username: string, password: string): Promise<LoginInfo> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload = {
        login: username,
        password,
        device_uuid: this.deviceService.getUuid(),
        device_model: this.deviceService.getModel(),
        device_manufacturer: this.deviceService.getManufacturer(),
      };
      const response = await this.client.post<ApiAuthUserResponse>(
        `${this.apiService.baseUrl()}/auth.user`,
        payload,
        {},
        true,
        false
      );

      const userInfo = new UserInfo(
        response.user.id,
        response.user.username,
        response.user.full_name,
        response.user.name,
        response.user.surname,
        response.user.email
      );
      const origins = response.origins.map((origin) => new OriginInfo(origin.code, origin.name));

      const attendanceConfiguration = AttendanceConfiguration.fromPrimitives(response.attendance);
      return new LoginInfo(userInfo, origins, response.token, attendanceConfiguration);
    } catch (exception) {
      // specific exceptions management
      if (exception instanceof HttpErrorResponse) {
        switch (exception.status) {
          case HttpStatusCode.Unauthorized:
            throw InvalidCredentialsException.fromError(exception);
        }
      }

      // general exceptions management
      throw this.exceptionManager.manage(exception);
    }
  }

  async loginCard(serialNumber: string): Promise<LoginInfo> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload = {
        card_serial_number: serialNumber,
        device_uuid: this.deviceService.getUuid(),
        device_model: this.deviceService.getModel(),
        device_manufacturer: this.deviceService.getManufacturer(),
      };
      const response = await this.client.post<ApiAuthUserResponse>(
        `${this.apiService.baseUrl()}/auth.card`,
        payload,
        {},
        true,
        false
      );

      const userInfo = new UserInfo(
        response.user.id,
        response.user.username,
        response.user.full_name,
        response.user.name,
        response.user.surname,
        response.user.email
      );
      const origins = response.origins.map((origin) => new OriginInfo(origin.code, origin.name));

      const attendanceConfiguration = AttendanceConfiguration.fromPrimitives(response.attendance);
      return new LoginInfo(userInfo, origins, response.token, attendanceConfiguration);
    } catch (exception) {
      // specific exceptions management
      if (exception instanceof HttpErrorResponse) {
        switch (exception.status) {
          case HttpStatusCode.Unauthorized:
            throw InvalidCredentialsException.fromError(exception);
        }
      }

      // general exceptions management
      throw this.exceptionManager.manage(exception);
    }
  }

  async loginUser(token: string, originCode: string): Promise<AuthInfo> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload = {
        token,
        origin: originCode,
        device_uuid: this.deviceService.getUuid(),
        device_model: this.deviceService.getModel(),
        device_manufacturer: this.deviceService.getManufacturer(),
      };
      const response = await this.client.post<ApiAuthLoginResponse>(
        `${this.apiService.baseUrl()}/auth.login`,
        payload,
        {},
        true,
        false
      );

      const authToken = response.auth_token;
      // const refreshToken = response.refresh_token; // not implemented yet
      const refreshToken = '...';
      return new AuthInfo(AuthToken.from(authToken), RefreshToken.from(refreshToken));
    } catch (exception) {
      // specific exceptions management
      if (exception instanceof HttpErrorResponse) {
        switch (exception.status) {
          case HttpStatusCode.Unauthorized:
            throw InvalidCredentialsException.fromError(exception);
          case HttpStatusCode.Conflict:
            throw MasterDataStructureInvalidException.fromError(exception);
        }
      }

      // general exceptions management
      throw this.exceptionManager.manage(exception);
    }
  }
}
